import { sfGet, sfPatch, sfPost } from "./salesforceApi";
import {
  fromSalesforceProjectPageComponent,
  toSalesforceProjectPageComponent,
} from "../schema/projectPageComponentSchema";
import { getEnv } from "../config/getEnv";
import { fetchDescribe } from "../hooks/usePicklist";
import { SOBJECT_PROJECT_PAGE_COMPONENT } from "../config/sobjectNames";

const PPC_RESOURCE =
  getEnv("REACT_APP_SF_PPC_RESOURCE") || "/ProjectPageComponent";

/**
 * Resolve a record-type name to its Salesforce ID via the cached describe call.
 * Returns "" if the name cannot be resolved.
 */
async function resolveRecordTypeId(recordTypeName) {
  if (!recordTypeName) return "";
  try {
    const describe = await fetchDescribe(SOBJECT_PROJECT_PAGE_COMPONENT);
    const rtInfos = describe?.recordTypeInfos || [];
    const match = rtInfos.find(
      (rt) => rt.name === recordTypeName && !rt.master,
    );
    return match?.recordTypeId || "";
  } catch {
    return "";
  }
}

/**
 * Fetch a single Project Page Component by Id via Apex REST.
 */
export async function fetchProjectPageComponent(projectPageComponentId) {
  if (!projectPageComponentId)
    throw new Error(
      "fetchProjectPageComponent requires a projectPageComponentId",
    );
  const data = await sfGet(`${PPC_RESOURCE}`, {
    id: String(projectPageComponentId),
  });
  const record = Array.isArray(data) ? data[0] : data;
  return fromSalesforceProjectPageComponent(record || {});
}

export async function fetchProjectPageComponentsByProjectPageId(projectPageId) {
  if (!projectPageId)
    throw new Error(
      "fetchProjectPageComponentsByProjectPageId requires a projectPageId",
    );
  const data = await sfGet(`${PPC_RESOURCE}`, { ppid: String(projectPageId) });
  return Array.isArray(data) ? data : [];
}

export async function searchProjectPageComponents(searchKey) {
  const key = String(searchKey || "").trim();
  if (key.length === 0) return [];
  const data = await sfGet(`${PPC_RESOURCE}`, { searchkey: key });
  return Array.isArray(data) ? data : [];
}

export async function fetchAllProjectPageComponents() {
  try {
    const data = await sfGet(`${PPC_RESOURCE}`, { searchkey: "all" });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw err;
  }
}

export async function patchProjectPageComponent(
  projectPageComponentId,
  uiRecord,
) {
  if (!projectPageComponentId)
    throw new Error("patchProjectPageComponent requires an id");

  let record = uiRecord || {};
  if (record.info?.recordTypeName) {
    const resolvedId = await resolveRecordTypeId(record.info.recordTypeName);
    if (resolvedId) {
      record = {
        ...record,
        info: { ...record.info, recordTypeId: resolvedId },
      };
    }
  }

  const payload = toSalesforceProjectPageComponent(record);
  return sfPatch(`${PPC_RESOURCE}`, payload, {
    params: { id: String(projectPageComponentId) },
  });
}

/**
 * Create a new individual Project Page Component
 * @param {Object} uiRecord - UI form data
 * @returns {Promise<Object>} Created project page component response
 */
export async function createIndividualProjectPageComponent(uiRecord) {
  try {
    console.log(
      "🎯 Creating individual project page component with data:",
      uiRecord,
    );

    const recordTypeId = await resolveRecordTypeId(uiRecord.recordType);

    const payload = {
      Project_Page__c: uiRecord.projectPageId || uiRecord.projectPage || "",
      RecordTypeId: recordTypeId,
      Component_Order__c: uiRecord.componentOrder
        ? Number(uiRecord.componentOrder)
        : null,
      Project_Page_Component_Desc__c: uiRecord.description || "",
      Headline_1__c: uiRecord.headline1 || "",
      Headline_2__c: uiRecord.headline2 || "",
      Headline_3__c: uiRecord.headline3 || "",
      Text_Area_1__c: uiRecord.textArea1 || "",
      Text_Area_2__c: uiRecord.textArea2 || "",
      Text_Area_3__c: uiRecord.textArea3 || "",
      Button_Type__c: uiRecord.buttonType || "",
      Button_Type_2__c: uiRecord.buttonType2 || "",
      Button_Type_3__c: uiRecord.buttonType3 || "",
      Button_Name_1__c: uiRecord.buttonName1 || "",
      Button_Name_2__c: uiRecord.buttonName2 || "",
      Button_Name_3__c: uiRecord.buttonName3 || "",
      Button_URL_1__c: uiRecord.buttonUrl1 || "",
      Button_URL_2__c: uiRecord.buttonUrl2 || "",
      Button_URL_3__c: uiRecord.buttonUrl3 || "",
      Image_Location_1__c: uiRecord.imageLocation1 || "",
      Image_Location_2__c: uiRecord.imageLocation2 || "",
      Image_Location_3__c: uiRecord.imageLocation3 || "",
      File_Download_URL_1__c: uiRecord.fileUrl1 || "",
      File_Download_URL_2__c: uiRecord.fileUrl2 || "",
      File_Download_URL_3__c: uiRecord.fileUrl3 || "",
    };

    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === null ||
        payload[key] === "" ||
        payload[key] === undefined
      ) {
        delete payload[key];
      }
    });

    console.log("📤 Sending payload to Salesforce:", payload);

    const response = await sfPost(`${PPC_RESOURCE}`, payload);

    console.log("✅ Project Page Component created successfully:", response);

    return response;
  } catch (error) {
    console.error("❌ Error creating project page component:", error);
    throw error;
  }
}
